import { useState, useEffect } from "react";
import { getApiUrl } from "../utils/api";
const API = getApiUrl();
import { Palette, Type, Check, RotateCcw } from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { toast } from "sonner";


// Predefined color palettes
const COLOR_PALETTES = [
  {
    id: "classic-red",
    name: "Clássico Vermelho",
    colors: {
      primary: "#B3121B",
      secondary: "#F3C9CF",
      accent: "#6E0F1A",
      background: "#FFFAF5"
    }
  },
  {
    id: "rose-gold",
    name: "Rosa Dourado",
    colors: {
      primary: "#B76E79",
      secondary: "#F8E8E8",
      accent: "#D4A574",
      background: "#FFF9F5"
    }
  },
  {
    id: "sage-green",
    name: "Verde Sálvia",
    colors: {
      primary: "#7D8B6E",
      secondary: "#E8EDE5",
      accent: "#4A5D45",
      background: "#FAFCF8"
    }
  },
  {
    id: "navy-gold",
    name: "Azul e Dourado",
    colors: {
      primary: "#1B365D",
      secondary: "#E8EEF4",
      accent: "#C9A227",
      background: "#F8FAFC"
    }
  },
  {
    id: "lavender",
    name: "Lavanda",
    colors: {
      primary: "#7B68A6",
      secondary: "#EDE8F5",
      accent: "#9B8BB8",
      background: "#FAF8FC"
    }
  },
  {
    id: "terracotta",
    name: "Terracota",
    colors: {
      primary: "#C2735C",
      secondary: "#F5E6E0",
      accent: "#8B5A42",
      background: "#FFFAF7"
    }
  },
  {
    id: "midnight",
    name: "Meia-Noite",
    colors: {
      primary: "#2C3E50",
      secondary: "#ECF0F1",
      accent: "#E74C3C",
      background: "#F8F9FA"
    }
  },
  {
    id: "blush",
    name: "Blush",
    colors: {
      primary: "#E8B4B8",
      secondary: "#FDF1F2",
      accent: "#D4838A",
      background: "#FFFBFC"
    }
  }
];

// Font options
const FONT_OPTIONS = [
  { id: "playfair", name: "Playfair Display", family: "'Playfair Display', serif", preview: "Elegante e Clássico" },
  { id: "cormorant", name: "Cormorant Garamond", family: "'Cormorant Garamond', serif", preview: "Sofisticado" },
  { id: "great-vibes", name: "Great Vibes", family: "'Great Vibes', cursive", preview: "Romântico" },
  { id: "lora", name: "Lora", family: "'Lora', serif", preview: "Tradicional" },
  { id: "dancing-script", name: "Dancing Script", family: "'Dancing Script', cursive", preview: "Divertido" },
  { id: "montserrat", name: "Montserrat", family: "'Montserrat', sans-serif", preview: "Moderno" },
  { id: "crimson", name: "Crimson Text", family: "'Crimson Text', serif", preview: "Literário" },
  { id: "josefin", name: "Josefin Sans", family: "'Josefin Sans', sans-serif", preview: "Minimalista" }
];

export default function ThemeCustomizer({ site, onUpdate }) {
  const [themeColors, setThemeColors] = useState(site?.theme_colors || COLOR_PALETTES[0].colors);
  const [fontHeading, setFontHeading] = useState(site?.theme_fonts?.heading || "playfair");
  const [fontBody, setFontBody] = useState(site?.theme_fonts?.body || "montserrat");
  const [saving, setSaving] = useState(false);
  const [selectedPalette, setSelectedPalette] = useState(null);

  useEffect(() => {
    if (site?.theme_colors) {
      setThemeColors(site.theme_colors);
      // Try to find matching palette
      const match = COLOR_PALETTES.find(p => 
        p.colors.primary === site.theme_colors.primary
      );
      if (match) setSelectedPalette(match.id);
    }
    if (site?.theme_fonts) {
      setFontHeading(site.theme_fonts.heading || "playfair");
      setFontBody(site.theme_fonts.body || "montserrat");
    }
  }, [site]);

  const handlePaletteSelect = (palette) => {
    setSelectedPalette(palette.id);
    setThemeColors(palette.colors);
  };

  const handleColorChange = (key, value) => {
    setSelectedPalette(null); // Custom color
    setThemeColors(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/sites/${site.site_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          theme_colors: themeColors,
          theme_fonts: {
            heading: fontHeading,
            body: fontBody
          }
        })
      });

      if (res.ok) {
        toast.success("Tema salvo com sucesso!");
        const updated = await res.json();
        if (onUpdate) onUpdate(updated);
      } else {
        throw new Error('Erro ao salvar');
      }
    } catch (error) {
      toast.error("Erro ao salvar tema");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const defaultPalette = COLOR_PALETTES[0];
    setSelectedPalette(defaultPalette.id);
    setThemeColors(defaultPalette.colors);
    setFontHeading("playfair");
    setFontBody("montserrat");
  };

  const getHeadingFont = () => FONT_OPTIONS.find(f => f.id === fontHeading)?.family || "'Playfair Display', serif";
  const getBodyFont = () => FONT_OPTIONS.find(f => f.id === fontBody)?.family || "'Montserrat', sans-serif";

  return (
    <div className="space-y-6">
      {/* Color Palettes */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="font-serif text-xl text-[#6E0F1A] flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Paleta de Cores
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Predefined Palettes */}
          <div>
            <Label className="text-[#6E0F1A] mb-3 block">Paletas Predefinidas</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {COLOR_PALETTES.map((palette) => (
                <button
                  key={palette.id}
                  onClick={() => handlePaletteSelect(palette)}
                  className={`relative p-3 rounded-xl border-2 transition-all ${
                    selectedPalette === palette.id 
                      ? 'border-[#B3121B] shadow-lg scale-105' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex gap-1 mb-2">
                    <div 
                      className="w-6 h-6 rounded-full border border-white shadow-sm" 
                      style={{ backgroundColor: palette.colors.primary }}
                    />
                    <div 
                      className="w-6 h-6 rounded-full border border-white shadow-sm" 
                      style={{ backgroundColor: palette.colors.secondary }}
                    />
                    <div 
                      className="w-6 h-6 rounded-full border border-white shadow-sm" 
                      style={{ backgroundColor: palette.colors.accent }}
                    />
                  </div>
                  <p className="text-xs font-medium text-gray-700">{palette.name}</p>
                  {selectedPalette === palette.id && (
                    <div className="absolute -top-2 -right-2 bg-[#B3121B] text-white rounded-full p-1">
                      <Check className="w-3 h-3" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Colors */}
          <div>
            <Label className="text-[#6E0F1A] mb-3 block">Cores Personalizadas</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Cor Principal</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={themeColors.primary || "#B3121B"}
                    onChange={(e) => handleColorChange('primary', e.target.value)}
                    className="w-10 h-10 rounded-lg cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={themeColors.primary || "#B3121B"}
                    onChange={(e) => handleColorChange('primary', e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border rounded-lg font-mono"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Cor Secundária</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={themeColors.secondary || "#F3C9CF"}
                    onChange={(e) => handleColorChange('secondary', e.target.value)}
                    className="w-10 h-10 rounded-lg cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={themeColors.secondary || "#F3C9CF"}
                    onChange={(e) => handleColorChange('secondary', e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border rounded-lg font-mono"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Cor de Destaque</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={themeColors.accent || "#6E0F1A"}
                    onChange={(e) => handleColorChange('accent', e.target.value)}
                    className="w-10 h-10 rounded-lg cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={themeColors.accent || "#6E0F1A"}
                    onChange={(e) => handleColorChange('accent', e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border rounded-lg font-mono"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Fundo</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={themeColors.background || "#FFFAF5"}
                    onChange={(e) => handleColorChange('background', e.target.value)}
                    className="w-10 h-10 rounded-lg cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={themeColors.background || "#FFFAF5"}
                    onChange={(e) => handleColorChange('background', e.target.value)}
                    className="flex-1 px-2 py-1 text-sm border rounded-lg font-mono"
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Font Selection */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="font-serif text-xl text-[#6E0F1A] flex items-center gap-2">
            <Type className="w-5 h-5" />
            Tipografia
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Heading Font */}
          <div>
            <Label className="text-[#6E0F1A] mb-3 block">Fonte dos Títulos</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {FONT_OPTIONS.slice(0, 4).map((font) => (
                <button
                  key={font.id}
                  onClick={() => setFontHeading(font.id)}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    fontHeading === font.id 
                      ? 'border-[#B3121B] shadow-lg bg-[#F3C9CF]/20' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p 
                    className="text-xl mb-1" 
                    style={{ fontFamily: font.family }}
                  >
                    {font.preview}
                  </p>
                  <p className="text-xs text-gray-500">{font.name}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Body Font */}
          <div>
            <Label className="text-[#6E0F1A] mb-3 block">Fonte do Texto</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {FONT_OPTIONS.slice(4).map((font) => (
                <button
                  key={font.id}
                  onClick={() => setFontBody(font.id)}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    fontBody === font.id 
                      ? 'border-[#B3121B] shadow-lg bg-[#F3C9CF]/20' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p 
                    className="text-base mb-1" 
                    style={{ fontFamily: font.family }}
                  >
                    {font.preview}
                  </p>
                  <p className="text-xs text-gray-500">{font.name}</p>
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card className="card-dashboard overflow-hidden">
        <CardHeader>
          <CardTitle className="font-serif text-xl text-[#6E0F1A]">Pré-visualização</CardTitle>
        </CardHeader>
        <CardContent>
          <div 
            className="rounded-xl p-8 text-center transition-all"
            style={{ backgroundColor: themeColors.background || "#FFFAF5" }}
          >
            <h2 
              className="text-3xl mb-4"
              style={{ 
                fontFamily: getHeadingFont(), 
                color: themeColors.primary || "#B3121B" 
              }}
            >
              {site?.partner1_name || "Nome"} & {site?.partner2_name || "Nome"}
            </h2>
            <p 
              className="text-lg mb-6"
              style={{ 
                fontFamily: getBodyFont(), 
                color: themeColors.accent || "#6E0F1A" 
              }}
            >
              Convidamos você para celebrar conosco
            </p>
            <button
              className="px-6 py-3 rounded-full text-white font-medium transition-all hover:opacity-90"
              style={{ backgroundColor: themeColors.primary || "#B3121B" }}
            >
              Confirmar Presença
            </button>
            <div 
              className="mt-6 p-4 rounded-lg"
              style={{ backgroundColor: themeColors.secondary || "#F3C9CF" }}
            >
              <p 
                style={{ 
                  fontFamily: getBodyFont(), 
                  color: themeColors.accent || "#6E0F1A" 
                }}
              >
                Este é um exemplo de como seu site ficará com as cores e fontes selecionadas.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3 justify-end">
        <Button 
          variant="outline" 
          onClick={handleReset}
          className="border-[#B3121B] text-[#B3121B] hover:bg-[#B3121B] hover:text-white"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Restaurar Padrão
        </Button>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-[#B3121B] hover:bg-[#9A0F17] text-white"
        >
          {saving ? "Salvando..." : "Salvar Tema"}
        </Button>
      </div>
    </div>
  );
}
